import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'

import {
  deserializeSwitchboardData,
  type SoundClipOption,
  type SwitchboardNode,
} from '@mangovoice/switchboard-ui'
import { ApiError, PaginatedApiResult } from '@mangovoice/types'

interface SoundClip {
  id: number
  uuid?: string
  name: string
  feature_code?: number
  location?: string
  pbx_id: number
  pbx_uuid?: string
  created_at?: string
  updated_at?: string
}

/**
 * Fetches all sound clips for a PBX and tracks which ones are referenced in the switchboard.
 * Sound clips are used in sound_clip, auto_attendant, and leave_voicemail nodes.
 */
export const useGetSoundClips = (
  pbxId: number | null,
  switchboardData?: { json_data?: string },
) => {
  const api = useMangoApiClient()

  const {
    data: soundClipsResponse,
    isLoading,
    refetch,
  } = useQuery<PaginatedApiResult<SoundClip> | null, ApiError>({
    queryKey: ['soundClips', pbxId],
    queryFn: async () => {
      if (!pbxId) return null

      const response = await api.switchboard.listSoundClips({
        pbx_id: pbxId,
        limit: 300,
      })

      if (response.ok && response.data) {
        return response.data
      }

      return null
    },
    enabled: !!pbxId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  })

  const soundClipIdsFromNodes = useMemo(() => {
    if (!switchboardData?.json_data) return []
    try {
      const flowData = deserializeSwitchboardData(switchboardData.json_data)
      const nodes = flowData?.nodes || []
      const soundClipIds = new Set<string>()

      nodes.forEach((node: SwitchboardNode) => {
        const nodeData = node.data as Record<string, unknown>
        const soundClipId = String(nodeData.soundClipId ?? '')
        if (
          ['sound_clip', 'auto_attendant', 'leave_voicemail'].includes(
            String(node.type),
          ) &&
          /^\d+$/.test(soundClipId)
        ) {
          soundClipIds.add(soundClipId)
        }
      })

      return Array.from(soundClipIds)
    } catch {
      return []
    }
  }, [switchboardData?.json_data])

  const filteredSoundClipsFromNodes = useMemo(() => {
    if (!soundClipsResponse?.results || soundClipIdsFromNodes.length === 0) {
      return []
    }

    return soundClipsResponse.results
      .filter((clip) => soundClipIdsFromNodes.includes(String(clip.id)))
      .map((clip) => ({
        id: String(clip.id),
        uuid: clip.uuid,
        name: clip.name || `Sound Clip ${clip.id}`,
      }))
  }, [soundClipsResponse?.results, soundClipIdsFromNodes])

  const soundClips = useMemo(() => {
    const generalClips =
      soundClipsResponse?.results?.map((clip) => ({
        id: String(clip.id),
        uuid: clip.uuid,
        name: clip.name || `Sound Clip ${clip.id}`,
      })) || []
    const nodeReferencedClips = filteredSoundClipsFromNodes || []
    const clipMap = new Map<string, SoundClipOption>()

    generalClips.forEach((clip) => {
      clipMap.set(clip.id, clip)
    })

    nodeReferencedClips.forEach((clip) => {
      clipMap.set(clip.id, clip)
    })

    soundClipIdsFromNodes.forEach((id) => {
      if (!clipMap.has(id)) {
        clipMap.set(id, {
          id,
          name: `Sound Clip ${id} (Not Found)`,
        })
      }
    })

    return Array.from(clipMap.values())
  }, [soundClipsResponse, filteredSoundClipsFromNodes, soundClipIdsFromNodes])

  return {
    soundClips,
    isLoading,
    refetch,
  }
}
